import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useVideoDashboardStore } from '@/store/videoDashboard/store';
import { useFilteredVideos } from '@/store/videoDashboard/filters';
import { DraggableVideoCard } from './DraggableVideoCard';
import { VideoCardSkeleton } from '../videos/VideoCardSkeleton';
// VideoList removido - usando VideoTablePremium para lista em todas as views
import { EmptyFolderState } from '../folders/EmptyFolderState';
import { EmptyState } from './EmptyState';
import { VideoModal } from '../videos/VideoModal';
import { FolderCard } from '@/components/hierarchical-folders/FolderCard';
import { FolderViewModal } from '@/components/hierarchical-folders/FolderViewModal';
// import { RenameFolderModal } from '@/components/hierarchical-folders/RenameFolderModal';
import { DeleteFolderModal } from '@/components/hierarchical-folders/DeleteFolderModal';
import { VideoTablePremium } from '@/components/hierarchical-folders/VideoTablePremium';
import { CopyCodeModal } from '@/components/shared/CopyCodeModal';
import { HierarchicalFolder, FolderDetails } from '@/types/hierarchicalFolders';
import { useHierarchicalFoldersStore } from '@/store/hierarchicalFoldersStore';
import { hierarchicalFoldersService } from '@/services/hierarchicalFoldersService';
import { nanoid } from 'nanoid';
import { Video } from '@/store/videoDashboard/types';
import { Video as HierarchicalVideo } from '@/types/hierarchicalFolders';
import { toast } from 'sonner';
import { t } from '@/utils/i18n';

interface DashboardContentProps {
  folderId?: string | null;
  currentFolder?: FolderDetails | null;
  rootFolders?: HierarchicalFolder[];
  isLoading?: boolean;
  onUploadClick: () => void;
  onFileSelect: (file: File) => void;
  onAddVideosClick?: () => void;
  isReadOnly?: boolean;
}

export function DashboardContent({ 
  folderId,
  currentFolder,
  rootFolders = [],
  isLoading: externalLoading = false,
  onUploadClick, 
  onFileSelect,
  onAddVideosClick,
  isReadOnly = false
}: DashboardContentProps) {
  useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState<{
    id: string;
    title: string;
    thumbnailUrl: string;
    viewCount: number;
    createdAt?: string;
  } | null>(null);
  
  // const [renamingFolder, setRenamingFolder] = useState<{ id: string; name: string } | null>(null);
  const [deletingFolderNames, setDeletingFolderNames] = useState<string[]>([]);
  const [foldersWithPlays, setFoldersWithPlays] = useState<HierarchicalFolder[]>([]);
  const [folderViewOpen, setFolderViewOpen] = useState(false);
  const [selectedFolderForView, setSelectedFolderForView] = useState<FolderDetails | null>(null);
  const [copyCodeVideo, setCopyCodeVideo] = useState<HierarchicalVideo | null>(null);
  
  const navigate = useNavigate();
  const { renameFolder, deleteFolders, fetchRootFolders } = useHierarchicalFoldersStore();
  const { viewMode, activeFolder, folders, addVideo, videos, densityMode } = useVideoDashboardStore(state => ({
    viewMode: state.viewMode,
    activeFolder: state.activeFolder,
    folders: state.folders,
    addVideo: state.addVideo,
    videos: state.videos,
    densityMode: state.densityMode
  }));
  
  const filteredVideos = useFilteredVideos();
  
  // Simulate loading state
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);
  
  useEffect(() => {
    console.log('DashboardContent mounted');
    console.log('Total videos:', videos.length);
    console.log('Filtered videos:', filteredVideos.length);
    console.log('Active folder:', activeFolder);
    console.log('View mode:', viewMode);
  }, [videos.length, filteredVideos.length, activeFolder, viewMode]);
  
  // Check if current folder has any assigned videos (not just filtered ones)
  const allVideos = useVideoDashboardStore(state => state.videos);
  const currentFolderHasVideos = activeFolder ? allVideos.some(v => v.folderId === activeFolder) : true;
  const activeFolder_data = activeFolder ? folders.find(f => f.id === activeFolder) : null;
  
  // Enrich folders with totalPlays
  useEffect(() => {
    const enrichFolders = async () => {
      if (rootFolders.length === 0) return;
      
      const enriched = await Promise.all(
        rootFolders.map(async (folder) => {
          try {
            const summary = await hierarchicalFoldersService.getPlaysSummary(folder.id);
            return { ...folder, totalPlays: summary.recursivePlays };
          } catch {
            return folder;
          }
        })
      );
      setFoldersWithPlays(enriched);
    };
    
    enrichFolders();
  }, [rootFolders]);
  
  // Handler para abrir o modal premium de visualização
  const handleFolderClick = (folder: HierarchicalFolder) => {
    // Navega para dentro da pasta no feed (não abre modal)
    navigate(`/meus-videos?folderId=${folder.id}`);
  };

  // Handler para renomear do modal premium
  const handleRenameFolderFromView = async (newName: string) => {
    if (!selectedFolderForView) return;
    try {
      await renameFolder(selectedFolderForView.id, newName);
      // Update the local state
      setSelectedFolderForView({ ...selectedFolderForView, name: newName });
      toast.success(translateInterfaceText("Pasta renomeada."));
    } catch (error) {
      console.error('Error renaming folder:', error);
    }
  };

  // Handler para deletar do modal premium
  const handleDeleteFolderFromView = async () => {
    if (!selectedFolderForView) return;
    try {
      await deleteFolders([selectedFolderForView.id]);
      setFolderViewOpen(false);
      setSelectedFolderForView(null);
      toast.success(translateInterfaceText("Pasta excluída."));
    } catch (error) {
      console.error('Error deleting folder:', error);
    }
  };
  
  // Inline renaming now - no longer need these handlers
  // const handleRenameFolder = (folder: HierarchicalFolder) => {
  //   setRenamingFolder({ id: folder.id, name: folder.name });
  // };
  
  // const handleConfirmRename = async (newName: string) => {
  //   if (!renamingFolder) return;
  //   try {
  //     await renameFolder(renamingFolder.id, newName);
  //     toast.success('Pasta renomeada.');
  //   } catch (error) {
  //     // Error toast already shown by store
  //   }
  // };
  
  const handleDeleteFolder = (folderId: string) => {
    const folders = folderId && currentFolder ? currentFolder.children : foldersWithPlays;
    const folder = folders.find(f => f.id === folderId);
    if (folder) {
      setDeletingFolderNames([folder.name]);
    }
  };
  
  const handleConfirmDelete = async () => {
    // Find the folder IDs based on the names
    const folders = folderId && currentFolder ? currentFolder.children : foldersWithPlays;
    const folderIds = deletingFolderNames
      .map(name => folders.find(f => f.name === name)?.id)
      .filter(Boolean) as string[];
    
    try {
      await deleteFolders(folderIds);
      toast.success(translateInterfaceText("Pasta excluída."));
      setDeletingFolderNames([]);
    } catch (error) {
      // Error toast already shown by store
    }
  };

  
  const handleFileSelect = (file: File) => {
    // Create a video URL from the file
    const videoUrl = URL.createObjectURL(file);
    
    // Generate a thumbnail (in a real app this would be done server-side)
    // For now we'll use a placeholder
    const thumbnailUrl = "https://placehold.co/600x400/purple/white?text=Video+Thumbnail";
    
    // Create a new video object
    const newVideo: Video = {
      id: `video-${nanoid()}`,
      title: file.name,
      description: "",
      thumbnailUrl,
      videoUrl,
      uploadDate: new Date().toISOString(),
      duration: 120, // placeholder duration in seconds
      views: 0,
      plays: 0,
      likes: 0,
      folderId: activeFolder || null,
      status: "processed", // In a real app this would be "processing" initially
      tags: [],
      createdAt: new Date().toISOString(),
      isNew: true,
      isProcessing: false,
      viewCount: 0
    };
    
    // Add the video to the store
    addVideo(newVideo);
    
    // Show success message
    toast.success(t('videoUploaded'));
  };

  // If in folder view and folder is empty
  const isFolderEmpty = folderId && currentFolder && 
    currentFolder.children.length === 0 && 
    currentFolder.videos.length === 0;

  // If we have no videos at all and not in folder view
  if (!folderId && videos.length === 0 && rootFolders.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="mt-8"
      >
        <EmptyState 
          title={translateInterfaceText("Bem-vindo ao HostVSL")}
          description={translateInterfaceText("Comece fazendo upload do seu primeiro vídeo.")}
          onAction={onUploadClick}
          isReadOnly={isReadOnly}
        />
      </motion.div>
    );
  }
  
  // Empty folder state
  if (isFolderEmpty) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
      >
        <EmptyFolderState 
          onUploadClick={onAddVideosClick || onUploadClick}
          className="my-8"
        />
      </motion.div>
    );
  }
  
  // Loading state
  if (isLoading || externalLoading) {
    return (
      <motion.div 
        className="grid gap-3"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <VideoCardSkeleton key={i} densityMode={densityMode} />
        ))}
      </motion.div>
    );
  }
  
  // Root view: show folders section + videos section
  if (!folderId) {
    return (
      <motion.div layout className="dashboard-content">
        <AnimatePresence>
          <div className="space-y-8">
            {/* Folders section */}
            {foldersWithPlays.length > 0 && (
              <div>
                <h2 className="text-[18px] font-semibold mb-3">{translateInterfaceText("Pastas")}</h2>
                 <div className="grid gap-4 justify-items-start" style={{ gridTemplateColumns: 'repeat(auto-fill, 240px)' }}>
                  {foldersWithPlays.map(folder => (
                    <FolderCard 
                      key={folder.id}
                      folder={folder}
                      onClick={() => handleFolderClick(folder)}
                      onRename={async (id, newName) => {
                        try {
                          await hierarchicalFoldersService.renameFolder(id, newName);
                          await fetchRootFolders();
                          toast.success(translateInterfaceText("Pasta renomeada com sucesso!"));
                        } catch (error) {
                          toast.error(translateInterfaceText("Erro ao renomear pasta"));
                        }
                      }}
                      onDelete={(id) => {
                        const folder = foldersWithPlays.find(f => f.id === id);
                        if (folder) setDeletingFolderNames([folder.name]);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Videos section */}
            {filteredVideos.length > 0 && (
              <div>
              <h2 className="text-[18px] font-semibold mb-3">{translateInterfaceText("Vídeos")}</h2>
              {viewMode === 'grid' ? (
                <motion.div 
                  className="grid gap-5 justify-items-start"
                  style={{ gridTemplateColumns: 'repeat(auto-fill, 280px)' }}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {filteredVideos.map((video) => (
                    <DraggableVideoCard
                      key={video.id}
                      id={video.id}
                      title={video.title}
                      thumbnail={video.thumbnailUrl}
                      viewCount={Number(video.views || video.viewCount || 0)}
                      isProcessing={video.isProcessing}
                      isNew={video.isNew}
                      folderId={video.folderId}
                      status={video.status}
                      createdAt={video.createdAt}
                      uploadDate={video.uploadDate}
                      onSelect={() => setSelectedVideo({
                        id: video.id,
                        title: video.title,
                        thumbnailUrl: video.thumbnailUrl,
                        viewCount: video.views || video.viewCount || 0,
                        createdAt: video.createdAt || video.uploadDate
                      })}
                    />
                  ))}
                </motion.div>
              ) : (
                <VideoTablePremium
                  videos={filteredVideos.map(v => ({
                    id: v.id,
                    title: v.title,
                    thumbnailUrl: v.thumbnailUrl || '',
                    plays: v.views || v.viewCount || 0,
                    createdAt: v.createdAt || v.uploadDate || new Date().toISOString(),
                    isProcessing: v.isProcessing || v.status === 'processing',
                    duration: v.duration || 0,
                    status: (v.status === 'failed' ? 'error' : v.status === 'processed' ? 'active' : 'processing') as 'processing' | 'active' | 'error'
                  }))}
                  onVideoClick={(video) => setSelectedVideo({
                    id: video.id,
                    title: video.title,
                    thumbnailUrl: video.thumbnailUrl,
                    viewCount: video.plays || 0,
                    createdAt: video.createdAt
                  })}
                  onVideoEdit={(video) => {
                    navigate(`/personalizations/${video.id}`);
                  }}
                  onVideoCopyCode={(video) => {
                    setCopyCodeVideo(video);
                  }}
                  onVideoStats={(video) => {
                    console.log('View stats for video:', video.id);
                  }}
                  onVideoDelete={async (videoId) => {
                    console.log('Delete video:', videoId);
                  }}
                />
              )}
              </div>
            )}
          </div>
        </AnimatePresence>

        <VideoModal
          open={!!selectedVideo}
          onOpenChange={(open) => !open && setSelectedVideo(null)}
          video={selectedVideo}
        />
        
        {/* Modal removido - Navegação inline sem modal flutuante */}
        
        {/* Renaming inline now - modal not needed */}
        {/* <RenameFolderModal
          open={!!renamingFolder}
          onOpenChange={(open) => !open && setRenamingFolder(null)}
          folder={renamingFolder}
          onConfirm={handleConfirmRename}
        /> */}
        
        <DeleteFolderModal
          open={deletingFolderNames.length > 0}
          onOpenChange={(open) => !open && setDeletingFolderNames([])}
          folderNames={deletingFolderNames}
          onConfirm={handleConfirmDelete}
        />
        
        {/* Show loading state when filter results in no videos but we have some in the store */}
        {filteredVideos.length === 0 && videos.length > 0 && !activeFolder && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-8 text-center"
          >
            <EmptyState 
              title={translateInterfaceText("Nenhum vídeo encontrado")}
              description={translateInterfaceText("Tente ajustar seus filtros de busca ou categoria.")}
            />
          </motion.div>
        )}
      </motion.div>
    );
  }
  
  // Folder view: unified grid of subfolders + videos
  if (currentFolder) {
    const folderVideos = currentFolder.videos.map(v => ({
      id: v.id,
      title: v.title,
      description: '',
      thumbnailUrl: v.thumbnailUrl || '',
      videoUrl: '',
      uploadDate: v.createdAt,
      duration: v.duration || 0,
      views: v.plays || 0,
      likes: 0,
      folderId: folderId,
      status: v.status,
      tags: [],
      createdAt: v.createdAt,
      isNew: false,
      isProcessing: v.status === 'processing',
      viewCount: v.plays || 0
    }));
    
    return (
      <motion.div layout className="dashboard-content">
        <AnimatePresence>
          <div className="space-y-6">
            {viewMode === 'grid' ? (
              <div className="space-y-6">
                {/* Bloco de Subpastas */}
                {currentFolder.children.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium mb-3">{translateInterfaceText("Subpastas")}</h3>
            <motion.div
              className="grid gap-4 justify-items-start"
              style={{ gridTemplateColumns: 'repeat(auto-fill, 240px)' }}
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
                      {currentFolder.children.map(folder => (
                        <FolderCard 
                          key={folder.id}
                          folder={folder}
                          onClick={() => handleFolderClick(folder)}
                        />
                      ))}
                    </motion.div>
                  </div>
                )}
                
                {/* Bloco de Vídeos */}
                {folderVideos.length > 0 && (
                  <div>
                    {currentFolder.children.length > 0 && <h3 className="text-sm font-medium mb-3">{translateInterfaceText("Vídeos")}</h3>}
                    <motion.div 
                      className="grid gap-5 justify-items-start"
                      style={{ gridTemplateColumns: 'repeat(auto-fill, 280px)' }}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      {folderVideos.map((video) => (
                        <DraggableVideoCard
                          key={video.id}
                          id={video.id}
                          title={video.title}
                          thumbnail={video.thumbnailUrl}
                          viewCount={Number(video.views || video.viewCount || 0)}
                          isProcessing={video.isProcessing}
                          isNew={video.isNew}
                          folderId={video.folderId}
                          status={video.status}
                          createdAt={video.createdAt}
                          uploadDate={video.uploadDate}
                          onSelect={() => setSelectedVideo({
                            id: video.id,
                            title: video.title,
                            thumbnailUrl: video.thumbnailUrl,
                            viewCount: video.views || video.viewCount || 0,
                            createdAt: video.createdAt || video.uploadDate
                          })}
                        />
                      ))}
                    </motion.div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Subfolders grid */}
                {currentFolder.children.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium mb-3">{translateInterfaceText("Subpastas")}</h3>
                    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
                      {currentFolder.children.map(folder => (
                        <FolderCard 
                          key={folder.id}
                          folder={folder}
                          onClick={() => handleFolderClick(folder)}
                        />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Videos table */}
                {folderVideos.length > 0 && (
                  <div>
                    {currentFolder.children.length > 0 && <h3 className="text-sm font-medium mb-3">{translateInterfaceText("Vídeos")}</h3>}
                    <VideoTablePremium
                      videos={currentFolder.videos}
                      onVideoClick={(video) => setSelectedVideo({
                        id: video.id,
                        title: video.title,
                        thumbnailUrl: video.thumbnailUrl || '',
                        viewCount: video.plays || 0,
                        createdAt: video.createdAt
                      })}
              onVideoEdit={(video) => {
                navigate(`/personalizations/${video.id}`);
              }}
                      onVideoStats={(video) => {
                        navigate(`/metrics?videoId=${video.id}`);
                      }}
                      onVideoCopyCode={(video) => {
                        setCopyCodeVideo(video);
                      }}
                      onVideoDelete={(video) => {
                        toast.info(translateInterfaceText("Funcionalidade de excluir em desenvolvimento"));
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </AnimatePresence>
        
        <VideoModal
          open={!!selectedVideo}
          onOpenChange={(open) => !open && setSelectedVideo(null)}
          video={selectedVideo}
        />
        
        {/* Modals */}
        <FolderViewModal
          open={folderViewOpen}
          onOpenChange={setFolderViewOpen}
          folder={selectedFolderForView}
          onRename={handleRenameFolderFromView}
          onDelete={handleDeleteFolderFromView}
          onNewVideo={() => navigate('/new-video')}
          onNewFolder={() => {
            setFolderViewOpen(false);
          }}
        />
        
        {/* Renaming inline now - modal not needed */}
        {/* <RenameFolderModal
          open={!!renamingFolder}
          onOpenChange={(open) => !open && setRenamingFolder(null)}
          folder={renamingFolder}
          onConfirm={handleConfirmRename}
        /> */}
        
        <DeleteFolderModal
          open={deletingFolderNames.length > 0}
          onOpenChange={(open) => !open && setDeletingFolderNames([])}
          folderNames={deletingFolderNames}
          onConfirm={handleConfirmDelete}
        />

        <CopyCodeModal
          isOpen={!!copyCodeVideo}
          onClose={() => setCopyCodeVideo(null)}
          entityId={copyCodeVideo?.id || ''}
          entityName={copyCodeVideo?.title || ''}
          entityType="video"
          jsCode={copyCodeVideo ? `<script src="https://app.hostvsl.com/embed.js"></script>\n<div data-video-id="${copyCodeVideo.id}"></div>` : ''}
        />
      </motion.div>
    );
  }
  
  // Fallback to original behavior
  return (
    <motion.div layout className="dashboard-content">
      <AnimatePresence>
        {filteredVideos.length > 0 ? (
          viewMode === 'grid' ? (
            <motion.div 
              className="grid gap-4"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {filteredVideos.map((video) => (
                <DraggableVideoCard
                  key={video.id}
                  id={video.id}
                  title={video.title}
                  thumbnail={video.thumbnailUrl}
                  viewCount={Number(video.views || video.viewCount || 0)}
                  isProcessing={video.isProcessing}
                  isNew={video.isNew}
                  folderId={video.folderId}
                  status={video.status}
                  createdAt={video.createdAt}
                  uploadDate={video.uploadDate}
                  onSelect={() => setSelectedVideo({
                    id: video.id,
                    title: video.title,
                    thumbnailUrl: video.thumbnailUrl,
                    viewCount: video.views || video.viewCount || 0,
                    createdAt: video.createdAt || video.uploadDate
                  })}
                />
              ))}
            </motion.div>
          ) : (
            <VideoTablePremium
              videos={filteredVideos.map(v => ({
                id: v.id,
                title: v.title,
                thumbnailUrl: v.thumbnailUrl || '',
                plays: v.views || v.viewCount || 0,
                createdAt: v.createdAt || v.uploadDate || new Date().toISOString(),
                isProcessing: v.isProcessing || v.status === 'processing',
                duration: v.duration || 0,
                status: (v.status === 'failed' ? 'error' : v.status === 'processed' ? 'active' : 'processing') as 'processing' | 'active' | 'error'
              }))}
              onVideoClick={(video) => setSelectedVideo({
                id: video.id,
                title: video.title,
                thumbnailUrl: video.thumbnailUrl,
                viewCount: video.plays || 0,
                createdAt: video.createdAt
              })}
              onVideoEdit={(video) => {
                navigate(`/personalizar?videoId=${video.id}`);
              }}
              onVideoCopyCode={(video) => {
                setCopyCodeVideo(video);
              }}
              onVideoStats={(video) => {
                console.log('View stats for video:', video.id);
              }}
              onVideoDelete={async (videoId) => {
                console.log('Delete video:', videoId);
              }}
            />
          )
        ) : activeFolder && activeFolder_data ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <EmptyFolderState 
              onUploadClick={onUploadClick}
              className="my-8"
            />
          </motion.div>
        ) : (
          <motion.div 
            className="mb-8 mt-12 animate-fade-in"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <EmptyState 
              title={translateInterfaceText("Nenhum vídeo encontrado")}
              description={translateInterfaceText("Comece fazendo upload do seu primeiro vídeo.")}
              onAction={onUploadClick}
              isReadOnly={isReadOnly}
            />
          </motion.div>
          )}
        </AnimatePresence>

      <VideoModal
        open={!!selectedVideo}
        onOpenChange={(open) => !open && setSelectedVideo(null)}
        video={selectedVideo}
      />
      
      {/* Modals */}
      <FolderViewModal
        open={folderViewOpen}
        onOpenChange={setFolderViewOpen}
        folder={selectedFolderForView}
        onRename={handleRenameFolderFromView}
        onDelete={handleDeleteFolderFromView}
        onNewVideo={() => navigate('/new-video')}
        onNewFolder={() => {
          setFolderViewOpen(false);
          // TODO: Open create folder modal with current folder as parent
        }}
      />
      
      {/* Renaming inline now - modal not needed */}
      {/* <RenameFolderModal
        open={!!renamingFolder}
        onOpenChange={(open) => !open && setRenamingFolder(null)}
        folder={renamingFolder}
        onConfirm={handleConfirmRename}
      /> */}
      
      <DeleteFolderModal
        open={deletingFolderNames.length > 0}
        onOpenChange={(open) => !open && setDeletingFolderNames([])}
        folderNames={deletingFolderNames}
        onConfirm={handleConfirmDelete}
      />
    </motion.div>
  );
}